<template>
  <div class="chart-transparency-index p-4 bg-muted" :class="{ 'chart-transparency-index--social-mode': socialMode }">
    <h4>{{ chartRoute.title }}</h4>
    <p>
      As part of the Shadow Diplomats investigation, ICIJ researched which countries <strong>host</strong> and
      <strong>appoint</strong>, and established a transparency index that measures how easy or difficult it
      is to access this information for each. ICIJ found that 78 of the countries did not make public the
      names of their honorary consuls.
    </p>
    <p>
      For a full breakdown of transparency indicators and to read more about this research, please <a :href='datasetLink' target="_parent" hreflang="en">see the data here</a>.
    </p>
    <b-input-group class="mb-4" size="sm">
      <b-input-group-prepend is-text>
        <fa icon="filter" />
      </b-input-group-prepend>
      <b-form-input v-model="filter" type="search" placeholder="Search by country"/>
    </b-input-group>
    <b-table
      :fields="fields"
      :dark="socialMode"
      :items="items"
      class="m-0"
      sort-by="country"
      small
      responsive
      table-class="chart-transparency-index__table small mb-3"
      tbody-tr-class="chart-transparency-index__table__row"
      thead-tr-class="chart-transparency-index__table__header">
      <template #cell(country)="{ item: { country } }">
        <span v-html="highlight(country)"></span>
      </template>
      <template #cell(hostsHonoraryConsuls)="{ item: { hostsHonoraryConsuls } }">
        <span v-if="hostsHonoraryConsuls === 'Y'" title="Yes">
          <fa aria-text="V" icon="check" class="text-success"/>
        </span>
        <span v-else-if="hostsHonoraryConsuls === 'N'" title="No">
          <fa aria-text="X" icon="xmark" class="text-danger"/>
        </span>
        <span v-else title="Not applicable">
          <span class="text-muted small">NA</span>
        </span>
      </template>
      <template #cell(appointsHonoraryConsuls)="{ item: { appointsHonoraryConsuls } }">
        <span v-if="appointsHonoraryConsuls === 'Y'" title="Yes">
          <fa icon="check" class="text-success"/>
        </span>
        <span v-else-if="appointsHonoraryConsuls === 'N'" title="No">
          <fa icon="xmark" class="text-danger"/>
        </span>
        <span v-else title="Not applicable">
          <span class="text-muted small">NA</span>
        </span>
      </template>
    </b-table>
    <p>
      About the data: The transparency index is based on information available as of August 2022.
      Countries marked with an asterisk don't use honorary consuls, based on ICIJ's and ProPublica's reporting.
      "<span class="text-muted">NA</span>" reflects countries for which there was no public information available on whether they host or appoint
      honorary consuls. "<fa aria-text="V" icon="check" class="text-success"/>" indicates a country appoints or hosts honorary
      consuls and "<fa aria-text="X" icon="xmark" class="text-danger"/>" indicates that the
      country doesn't appoint or host them. For more information,
      <a :href="datasetLink" target="_parent" hreflang="en">please see the full dataset here</a>.
    </p>
  </div>
</template>

<script>
import * as d3 from 'd3'
import Fuse from 'fuse.js'
import { camelCase, escapeRegExp, mapKeys } from 'lodash'
import chart from '../../mixins/chart'

export default {
  mixins: [chart],
  name: 'ChartTransparencyIndex',
  filters: {
    formatNumber: d3.format(',')
  },
  data () {
    return {
      filter: ''
    }
  },
  methods: {
    highlight (text) {
      if (this.filter && text) {
        const regex = new RegExp('(' + escapeRegExp(this.filter) + ')', 'gi')
        return text.replace(regex, '<mark class="p-0">$1</mark>')
      }
      return text
    },
    transparencyScoreColor (score) {
      if (isNaN(score)) {
        return '#fff'
      }

      return this.transparencyScoreColorScale(score)
    }
  },
  computed: {
    datasetLink () {
      return 'https://docs.google.com/spreadsheets/d/e/2PACX-1vT_CoNs9zantY_EkqeKGFkvbkHdHluf_tDOsxf5qsXhxGOUkpzZR3kuCA4krfPS8DeuKEjRRTVFq8rc/pubhtml'
    },
    transparencyScoreColorScale () {
      return d3.scaleSequential().domain([1, 13]).interpolator(d3.interpolateGreens)
    },
    tsv () {
      return this.$options.tsv.map(row => {
        return mapKeys(row, (_, k) => camelCase(k))
      })
    },
    fuse () {
      return new Fuse(this.tsv, {
        includeScore: false,
        includeMatches: false,
        shouldSort: false,
        threshold: 0.1,
        keys: ['country']
      })
    },
    tsvFiltered () {
      if (this.filter) {
        return this.fuse.search(this.filter).map(match => match.item)
      }
      return this.tsv
    },
    items () {
      return this.tsvFiltered
    },
    fields () {
      return [
        {
          key: 'country',
          sortable: true,
          label: 'Country',
          thClass: '--country',
          tdClass: '--country'
        },
        {
          key: 'hostsHonoraryConsuls',
          sortable: false,
          label: 'Hosts?',
          thClass: '--hostsHonoraryConsuls text-center',
          tdClass: '--hostsHonoraryConsuls text-center'
        },
        {
          key: 'appointsHonoraryConsuls',
          sortable: false,
          label: 'Appoints?',
          thClass: '--appointsHonoraryConsuls text-center',
          tdClass: '--appointsHonoraryConsuls text-center'
        }
      ]
    }
  }
}
</script>

<style lang="scss">
@import '../../variables.scss';

.chart-transparency-index {

  &__table {
    border: 1px solid $table-border-color;

    .chart-transparency-index--social-mode & {
      border: 0;

      &__header {
        background: $table-dark-head-bg;
      }
    }

    &__header {
      background: $table-head-bg;
      color: #fff;

      table.b-table.table thead > & > th {
        padding-right: $spacer !important;
        vertical-align: middle;

        &[aria-sort] {
          background-image: $b-table-sort-icon-bg-dark-not-sorted;
        }

        &[aria-sort=ascending] {
          background-image: $b-table-sort-icon-bg-dark-ascending;
        }

        &[aria-sort=descending] {
          background-image: $b-table-sort-icon-bg-dark-descending;
        }
      }
    }

    &__row {
      white-space: nowrap;

      td.--country {
        font-weight: bold;
      }

      td.--score {
        position: relative;
        vertical-align: middle;

        .badge {
          font-size: 1em;
          border: $border-color 1px solid;
          min-width: 2.5em;

          .chart-transparency-index--social-mode & {
            border-color: transparent;
          }
        }
      }
    }
  }
}
</style>

<tsv>
country	hostsHonoraryConsuls	appointsHonoraryConsuls
Afghanistan	unclear	Y
Albania	Y	Y
Algeria*	N	N
Andorra	Y	Y
Angola	Y	unclear
Antigua and Barbuda	unclear	unclear
Argentina	Y	unclear
Armenia	Y	Y
Australia	Y	Y
Austria	Y	Y
Azerbaijan	Y	Y
Bahamas	Y	Y
Bahrain	Y	Y
Bangladesh	Y	Y
Barbados	Y	Y
Belarus	Y	Y
Belgium	Y	Y
Belize	Y	Y
Benin	Y	Y
Bhutan	Y	Y
Bolivia	Y	Y
Bosnia and Herzegovina	Y	Y
Botswana	unclear	Y
Brazil	Y	Y
Brunei Darussalam	Y	unclear
Bulgaria	Y	Y
Burkina Faso	unclear	Y
Burundi	Y	Y
Cabo Verde	unclear	Y
Cambodia	Y	Y
Cameroon	unclear	Y
Canada	Y	Y
Central African Republic	unclear	Y
Chad	unclear	Y
Chile	Y	Y
China	Y	unclear
Colombia	Y	Y
Comoros	unclear	Y
Democratic Republic of Congo	unclear	Y
Republic of Congo	Y	Y
Costa Rica	Y	Y
Côte d'Ivoire	unclear	Y
Croatia	Y	Y
Cuba	Y	Y
Cyprus	Y	Y
Czechia	Y	Y
Denmark	Y	Y
Djibouti	unclear	Y
Dominica	Y	unclear
Dominican Republic	Y	Y
Ecuador	Y	Y
Egypt	Y	unclear
El Salvador	Y	Y
Equatorial Guinea	Y	unclear
Eritrea	Y	Y
Estonia	Y	Y
Eswatini	Y	unclear
Ethiopia	unclear	Y
Fiji	unclear	Y
Finland	Y	Y
France	Y	Y
Gabon	unclear	Y
Gambia	unclear	Y
Georgia	unclear	Y
Germany	Y	Y
Ghana	unclear	Y
Greece	Y	Y
Grenada	Y	Y
Guatemala	Y	Y
Guinea	Y	Y
Guinea-Bissau	unclear	Y
Guyana	Y	Y
Haiti	Y	Y
The Holy See*	N	N
Honduras	Y	Y
Hungary	Y	Y
Iceland	Y	Y
India	Y	Y
Indonesia	unclear	Y
Iran	Y	unclear
Iraq	Y	unclear
Ireland	Y	Y
Israel	Y	Y
Italy	Y	Y
Jamaica	Y	Y
Japan	Y	Y
Jordan	Y	Y
Kazakhstan	Y	Y
Kenya	Y	unclear
Kiribati	unclear	Y
North Korea*	N	N
South Korea	unclear	Y
Kosovo	Y	Y
Kuwait	Y	unclear
Kyrgyzstan	Y	unclear
Laos	unclear	Y
Latvia	Y	Y
Lebanon	Y	Y
Lesotho	unclear	Y
Liberia	unclear	Y
Libya	Y	unclear
Liechtenstein	Y	Y
Lithuania	Y	Y
Luxembourg	Y	Y
Madagascar	Y	Y
Malawi	unclear	Y
Malaysia	Y	Y
Maldives	unclear	Y
Mali	unclear	Y
Malta	Y	Y
Marshall Islands	Y	unclear
Mauritania	unclear	Y
Mauritius	Y	Y
Mexico	Y	Y
Micronesia	Y	unclear
Moldova	Y	Y
Monaco	Y	Y
Mongolia	Y	Y
Montenegro	Y	Y
Morocco	unclear	Y
Mozambique	Y	Y
Myanmar	Y	Y
Namibia	unclear	Y
Nauru*	N	N
Nepal	Y	Y
Netherlands	Y	Y
New Zealand	Y	Y
Nicaragua	Y	Y
Niger	Y	unclear
Nigeria	Y	unclear
North Macedonia	unclear	Y
Norway	Y	Y
Oman	Y	Y
Pakistan	unclear	Y
Palau	unclear	Y
Palestine	Y	unclear
Panama	unclear	Y
Papua New Guinea	Y	Y
Paraguay	Y	Y
Peru	Y	Y
Philippines	Y	Y
Poland	Y	Y
Portugal	Y	Y
Qatar*	N	N
Romania	Y	Y
Russia	Y	Y
Rwanda	Y	Y
Saint Kitts and Nevis	Y	Y
Saint Lucia	Y	Y
Saint Vincent and the Grenadines	Y	Y
Samoa	unclear	Y
San Marino	Y	Y
Sao Tome and Principe	unclear	Y
Saudi Arabia	Y	unclear
Senegal	unclear	Y
Serbia	Y	Y
Seychelles	Y	Y
Sierra Leone	Y	Y
Singapore	Y	Y
Slovakia	Y	Y
Slovenia	Y	Y
Solomon Islands	unclear	Y
Somalia	unclear	Y
South Africa	Y	Y
South Sudan	unclear	Y
Spain	Y	Y
Sri Lanka	Y	Y
Sudan	Y	unclear
Suriname	unclear	Y
Sweden	Y	Y
Switzerland	Y	Y
Syria	Y	Y
Tajikistan	unclear	Y
Tanzania	unclear	Y
Thailand	Y	unclear
Timor-Leste	unclear	Y
Togo	unclear	Y
Tonga	unclear	Y
Trinidad and Tobago	Y	Y
Tunisia	Y	unclear
Turkey	Y	Y
Turkmenistan	unclear	Y
Tuvalu	unclear	Y
Uganda	Y	Y
Ukraine	Y	Y
United Arab Emirates*	N	N
United Kingdom	Y	Y
United States	Y	N
Uruguay	Y	Y
Uzbekistan	Y	unclear
Vanuatu	unclear	Y
Venezuela	Y	unclear
Viet Nam	unclear	Y
Yemen	unclear	Y
Zambia	Y	unclear
Zimbabwe	Y	Y
</tsv>
